import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API } from "@/utils/constants";

const fmt = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(v || 0);
export const isLowStock = (p) => (p.low_stock_threshold || 0) > 0 && (p.stock || 0) <= p.low_stock_threshold;

export const CRMProducts = ({ refreshKey }) => {
  const [products, setProducts] = useState(null);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/crm/products`); setProducts(r.data); }
    catch { setProducts([]); toast.error("Failed to load products"); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { name: editor.name, sku: editor.sku || "", unit_price: parseFloat(editor.unit_price) || 0, stock: parseFloat(editor.stock) || 0, low_stock_threshold: parseFloat(editor.low_stock_threshold) || 0, currency: editor.currency || "USD" };
    try {
      if (editor.id) await axios.put(`${API}/crm/products/${editor.id}`, payload);
      else await axios.post(`${API}/crm/products`, payload);
      setEditor(null); load(); toast.success("Product saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save product"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/crm/products/${id}`); load(); }
    catch { toast.error("Failed to delete"); }
  };

  if (products === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const lowCount = products.filter(isLowStock).length;

  return (
    <div data-testid="crm-products">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          {products.length} product{products.length === 1 ? "" : "s"}
          {lowCount > 0 && <span className="ml-2 text-amber-600 font-medium" data-testid="low-stock-count"><AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />{lowCount} low on stock</span>}
        </p>
        <Button size="sm" onClick={() => setEditor({ name: "", sku: "", unit_price: "", stock: "", low_stock_threshold: "", currency: "USD" })} data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" />Product</Button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-16">
          <Package className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No products yet — add what you sell to track stock and add them to quotes, orders and invoices in one click.</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Product</th><th className="text-left p-3 font-medium hidden sm:table-cell">SKU</th><th className="text-right p-3 font-medium">Price</th><th className="text-right p-3 font-medium">In stock</th><th className="p-3 w-20"></th></tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t hover:bg-muted/30" data-testid={`product-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-muted-foreground hidden sm:table-cell">{p.sku || "—"}</td>
                  <td className="p-3 text-right">{fmt(p.unit_price, p.currency)}</td>
                  <td className="p-3 text-right">
                    <span className={`font-semibold ${isLowStock(p) ? "text-amber-600" : ""}`} data-testid={`product-stock-${p.id}`}>
                      {p.stock ?? 0}
                      {isLowStock(p) && <AlertTriangle className="h-3.5 w-3.5 inline ml-1 -mt-0.5" />}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditor({ ...p })} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500" data-testid={`delete-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="product-drawer">
          <SheetHeader><SheetTitle>{editor?.id ? "Edit Product" : "New Product"}</SheetTitle></SheetHeader>
          {editor && (
            <form onSubmit={save} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Name</label>
                <Input required placeholder="Product or service name" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="product-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">SKU</label>
                  <Input placeholder="SKU-001" value={editor.sku} onChange={(e) => setEditor({ ...editor, sku: e.target.value })} data-testid="product-sku" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Currency</label>
                  <Select value={editor.currency || "USD"} onValueChange={(v) => setEditor({ ...editor, currency: v })}>
                    <SelectTrigger data-testid="product-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["USD", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Unit price</label>
                  <Input required type="number" min="0" step="0.01" placeholder="0.00" value={editor.unit_price} onChange={(e) => setEditor({ ...editor, unit_price: e.target.value })} data-testid="product-price" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">In stock</label>
                  <Input type="number" min="0" step="1" placeholder="0" value={editor.stock} onChange={(e) => setEditor({ ...editor, stock: e.target.value })} data-testid="product-stock" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Low-stock alert</label>
                  <Input type="number" min="0" step="1" placeholder="0" value={editor.low_stock_threshold} onChange={(e) => setEditor({ ...editor, low_stock_threshold: e.target.value })} data-testid="product-threshold" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">Stock decreases automatically when a sales order with this product is marked fulfilled.</p>
              <Button type="submit" className="w-full" data-testid="save-product-btn">Save Product</Button>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
